import { t } from '@lingui/macro'

/**
 * The CIOX request statuses, ordered least to most advanced.
 *
 * A patient holds one CIOX request per provider, so the patient list shows a single
 * rolled-up status: how far the furthest-along request has progressed. The backend
 * ranks by this same order (APP-1718).
 */
export const CIOX_STATUSES = ['NONE', 'SENT_TO_CIOX', 'CIOX_RETRIEVAL_FAILED', 'RECORDS_RECEIVED_VIA_CIOX'] as const

export type CioxStatus = (typeof CIOX_STATUSES)[number]

/**
 * Labels are built on call rather than at module level so they follow the active locale.
 */
export const getCioxStatusOptions = (): { value: CioxStatus; label: string }[] => [
    { value: 'NONE', label: t`None` },
    { value: 'SENT_TO_CIOX', label: t`Sent to Ciox` },
    { value: 'RECORDS_RECEIVED_VIA_CIOX', label: t`Records Received via Ciox` },
    { value: 'CIOX_RETRIEVAL_FAILED', label: t`Ciox Retrieval Failed` }
]

/**
 * Synthetic status meaning the patient has no CIOX request row at all.
 *
 * A request is created with status NONE — the Add form only collects a provider name —
 * so "no request" and "request not yet sent" are different answers to "does this patient
 * still need one?" and must stay distinguishable in the list.
 */
export const CIOX_NOT_REQUESTED = 'NOT_REQUESTED'

/**
 * The values the patient list can show and filter on, most urgent first.
 */
export const getCioxListStatusOptions = (): { value: string; label: string }[] => [
    { value: CIOX_NOT_REQUESTED, label: t`Not requested` },
    ...getCioxStatusOptions()
]

/**
 * Label for a status coming off the API, which types it as a plain string.
 * An unrecognised value is shown as-is rather than silently blanked.
 */
export const getCioxStatusLabel = (status: string | null | undefined): string => {
    if (!status) return t`Not requested`
    return getCioxListStatusOptions().find((option) => option.value === status)?.label ?? status
}
